/**
 * StarshipitLabelPrefetchSection — per-location "buy the StarShipIt label at
 * packing scan" (BLU-882).
 *
 * ON: the moment an order is scanned at the packing station, the backend asks
 * StarShipIt for its label and holds it. The Print click ~20 s later serves
 * that label instantly instead of waiting ~5 s on the carrier. A label that is
 * never printed is archived at 11 PM Sydney so it is never manifested or
 * billed. If the order changes between scan and print, a fresh label is issued.
 *
 * Default OFF: this spends a carrier label before the packer has committed,
 * and it requires the org's StarShipIt write-back to be set to "on manifest"
 * (otherwise Shopify is told "shipped" at scan time). So a floor opts in.
 *
 * Mirrors OrderHoldSettingsSection (embedded + locationId props,
 * getLocationSettings query, per-row pending spinner, toast + refetch, Switch
 * per location). Only StarShipIt floors are actionable; others show why.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { MapPin, Loader2, AlertTriangle, RefreshCw, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface StarshipitLabelPrefetchSectionProps {
  embedded?: boolean;
  locationId?: string;
}

export function StarshipitLabelPrefetchSection({
  embedded = false,
  locationId,
}: StarshipitLabelPrefetchSectionProps) {
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();

  const setMutation = trpc.productionSettings.setLocationStarshipitLabelPrefetch.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(variables.enabled ? 'Pre-made labels enabled' : 'Pre-made labels disabled');
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update pre-made label setting: ${error.message}`);
    },
  });

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  const locations = locationId ? allLocations.filter((l) => l.id === locationId) : allLocations;

  const content = (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <p className="text-sm text-muted-foreground">
          When an order is scanned at packing, ask StarShipIt for its label straight away so the
          Print button is instant. Labels that are never printed are archived at 11 PM Sydney and
          are not billed. Requires StarShipIt&apos;s write-back to be set to &quot;on manifest&quot;
          (StarShipIt → Settings → Integrations).
        </p>
        {/* YunExpress has the same switch, but it lives with the location's
            shipping product, because it cannot be on without one. */}
        <p className="text-xs text-muted-foreground">
          <Trans
            i18nKey="production.starshipitPrefetch.yunexpressPointer"
            ns="settings"
            components={{
              link: (
                <Link
                  to="/production/settings?area=locations#shipping-provider"
                  className="underline hover:text-foreground"
                />
              ),
            }}
          />
        </p>
      </div>

      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load locations</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message || 'Unknown error'}
            <Button variant="link" className="p-0 h-auto" onClick={() => locationsQuery.refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="ml-2 text-muted-foreground">Loading locations...</span>
        </div>
      )}

      {!isLoading && !hasError && locations.length === 0 && (
        <div className="flex flex-col items-center py-8 text-center bg-muted/30 rounded-lg">
          <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
          <p className="font-medium">No locations configured</p>
        </div>
      )}

      {!isLoading && !hasError && locations.length > 0 && (
        <div className="space-y-3" role="list" aria-label="Per-location pre-made shipping label">
          {locations.map((location) => {
            const enabled = location.starshipitLabelPrefetchEnabled;
            // Location default (org fallback included) OR an active client override.
            // A stale ON at a floor that no longer ships StarShipIt stays clearable.
            const isStarshipit = location.shipsStarshipit;
            const canToggle = isStarshipit || enabled;
            const rowPending =
              setMutation.isPending && setMutation.variables?.locationId === location.id;
            const switchId = `starshipit-prefetch-${location.id}`;
            return (
              <div key={location.id} role="listitem" className="overflow-hidden rounded-lg bg-muted/30">
                <div className="flex items-center justify-between p-3 transition-colors hover:bg-muted/50">
                  <div>
                    {!locationId && <p className="font-medium">{location.name}</p>}
                    <NoteLine icon={Zap} className="mt-1">
                      {!isStarshipit
                        ? 'Only applies to locations shipping via StarShipIt'
                        : enabled
                          ? 'Label bought at packing scan; Print is instant'
                          : 'Label bought when Print is clicked'}
                    </NoteLine>
                  </div>

                  <div className="flex items-center gap-2">
                    {rowPending && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
                    )}
                    <Label htmlFor={switchId} className="text-xs font-normal text-muted-foreground">
                      Pre-made label
                    </Label>
                    <Switch
                      id={switchId}
                      checked={enabled}
                      onCheckedChange={(next) =>
                        setMutation.mutate({ locationId: location.id, enabled: next })
                      }
                      disabled={rowPending || !canToggle}
                      aria-label={`Pre-made shipping label for ${location.name}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div role="status" aria-live="polite" className="sr-only">
        {setMutation.isSuccess && 'Pre-made label setting updated'}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-lg">Pre-made shipping label</CardTitle>
        </div>
        <CardDescription>
          Buy the StarShipIt label at packing scan so the Print click is instant.
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
